/**
 * The widget composer: a pill with an attach button, an auto-growing textarea,
 * and a right slot that is a mic while the field is empty and a send button as
 * soon as there is text.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUp, AudioLines, FileText, Image as ImageIcon, Loader2, Plus, Square, X } from 'lucide-react';

import { MAX_FILES, MAX_FILE_SIZE, useUploadFiles } from '@/api/alfie';
import { useDictation } from './use-dictation';

const MAX_TEXTAREA_HEIGHT = 140;

interface WidgetComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (text: string, fileIds?: string[]) => void;
  /** Aborts the in-flight answer. Backs the stop button. */
  onStop?: () => void;
  /** An answer is streaming: the send slot becomes a stop button. */
  isStreaming?: boolean;
  disabled?: boolean;
  /** Focus target when the widget opens. */
  inputRef: React.RefObject<HTMLTextAreaElement>;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function WidgetComposer({
  value,
  onChange,
  onSend,
  onStop,
  isStreaming,
  disabled,
  inputRef,
}: WidgetComposerProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadFiles = useUploadFiles();

  const dictation = useDictation(
    useCallback(
      (text: string) => onChange(value ? `${value.replace(/\s$/, '')} ${text}` : text),
      // `value` is read fresh on every transcript, so it must stay a dep.
      [onChange, value],
    ),
  );

  // Auto-grow: reset to auto first so the box can also SHRINK when text is
  // deleted, then clamp at MAX_TEXTAREA_HEIGHT and let it scroll.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
  }, [value, inputRef]);

  const hasText = value.trim().length > 0;

  const handleSend = useCallback(async () => {
    const text = value.trim();
    if (!text || disabled || isUploading) return;

    let fileIds: string[] | undefined;
    if (files.length > 0) {
      setIsUploading(true);
      try {
        const uploaded = await uploadFiles.mutateAsync({ files });
        fileIds = uploaded.map((f) => f.id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed');
        setIsUploading(false);
        return;
      }
      setIsUploading(false);
    }

    dictation.stop();
    setFiles([]);
    setError(null);
    onChange('');
    onSend(text, fileIds);
  }, [value, disabled, isUploading, files, uploadFiles, dictation, onChange, onSend]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // `isComposing` guards IME candidate selection — pressing Enter to accept a
    // Japanese/Chinese candidate must not fire the message.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void handleSend();
    }
  };

  const handleFilesPicked = (picked: FileList | null) => {
    // Cleared on EVERY path, rejections included: the input keeps the rejected
    // selection otherwise, and re-picking the same file fires no change event,
    // so the second attempt looks like a dead button.
    const clearPicker = () => {
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    if (!picked?.length) {
      clearPicker();
      return;
    }
    const next = Array.from(picked);
    if (files.length + next.length > MAX_FILES) {
      setError(`Up to ${MAX_FILES} files per message.`);
      clearPicker();
      return;
    }
    const tooBig = next.find((f) => f.size > MAX_FILE_SIZE);
    if (tooBig) {
      setError(`${tooBig.name} is over the 25 MB limit.`);
      clearPicker();
      return;
    }
    setError(null);
    setFiles((prev) => [...prev, ...next]);
    clearPicker();
  };

  const iconButton =
    'flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors';

  return (
    <div className="shrink-0 px-3 pt-2 pb-[calc(0.75rem+var(--safe-bottom))]">
      {files.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-2">
          {files.map((file, index) => {
            const Icon = file.type.startsWith('image/') ? ImageIcon : FileText;
            return (
              <li
                key={`${file.name}-${index}`}
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-muted px-2.5 py-1.5"
              >
                <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="max-w-[140px] truncate text-xs font-medium text-foreground">
                  {file.name}
                </span>
                <span className="text-[11px] text-muted-foreground">{formatSize(file.size)}</span>
                <button
                  type="button"
                  onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                  aria-label={`Remove ${file.name}`}
                  className="rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <p role="alert" className="mb-2 px-1 text-[11px] text-destructive">
          {error}
        </p>
      )}

      {/* py is 5px, not 6: the 1px border counts toward the box, so 36px
          buttons + 12px padding + 2px border would render a 50px pill where
          the design calls for 48. */}
      <div className="flex min-h-[48px] items-end gap-1.5 rounded-[24px] border border-border bg-muted px-1.5 py-[5px]">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleFilesPicked(e.target.files)}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          aria-label="Attach a file"
          className={`${iconButton} text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground`}
        >
          <Plus className="h-[18px] w-[18px]" strokeWidth={1.75} />
        </button>

        <textarea
          ref={inputRef}
          rows={1}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={dictation.isDictating ? 'Listening…' : 'Ask Alfie…'}
          // 16px on mobile: anything smaller makes iOS Safari zoom the page on
          // focus and never zoom back out.
          className="my-1.5 max-h-[140px] min-w-0 flex-1 resize-none bg-transparent text-base leading-[20px] text-foreground outline-none placeholder:text-muted-foreground sm:text-sm sm:leading-[18px]"
        />

        {isStreaming ? (
          // Never `disabled`: the whole point is that it stays reachable for
          // the length of the stream, which is exactly when the composer's
          // other controls are inert.
          <button
            type="button"
            onClick={onStop}
            aria-label="Stop generating"
            className={`${iconButton} bg-primary text-primary-foreground hover:bg-primary/90`}
          >
            <Square className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} />
          </button>
        ) : hasText ? (
          <button
            type="button"
            onClick={() => void handleSend()}
            disabled={disabled || isUploading}
            aria-label="Send message"
            className={`${iconButton} bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60`}
          >
            {isUploading ? (
              <Loader2 className="h-[18px] w-[18px] animate-spin" />
            ) : (
              <ArrowUp className="h-[18px] w-[18px]" strokeWidth={2} />
            )}
          </button>
        ) : (
          dictation.isSupported && (
            <button
              type="button"
              onClick={dictation.toggle}
              aria-label={dictation.isDictating ? 'Stop dictation' : 'Dictate a message'}
              aria-pressed={dictation.isDictating}
              className={`${iconButton} ${
                dictation.isDictating
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-foreground text-background hover:bg-foreground/90'
              }`}
            >
              <AudioLines className="h-[18px] w-[18px]" strokeWidth={1.75} />
            </button>
          )
        )}
      </div>

      <p className="mt-2 text-center text-[11px] leading-[14px] text-muted-foreground">
        Alfie can make mistakes. Check important numbers.
      </p>
    </div>
  );
}

export default WidgetComposer;
